import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { queries } from '../lib/queries'
import { polling } from '../lib/polling'
import * as Sentry from '@sentry/tanstackstart-react'

export const deviceKeys = {
  all: queries.devices.all,
  lists: queries.devices.lists,
  list: queries.devices.list,
  details: queries.devices.details,
  detail: queries.devices.detail,
  detected: () => [...queries.devices.all, 'detected'] as const,
  policies: queries.devices.policies,
  status: queries.devices.status,
}

export function useDevices(options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: deviceKeys.list(),
    queryFn: () => api.getDevices(),
    staleTime: 0,
    gcTime: 0,
    refetchInterval: options?.refetchInterval ?? polling.deviceList,
  })
}

export function useDevice(id: string, options?: { refetchInterval?: number | false; enabled?: boolean }) {
  return useQuery({
    queryKey: deviceKeys.detail(id),
    queryFn: () => api.getDevice(id),
    enabled: options?.enabled !== false && !!id,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: options?.refetchInterval,
  })
}

export function useDetectedDevices(options?: { enabled?: boolean; refetchInterval?: number | false }) {
  return useQuery({
    queryKey: deviceKeys.detected(),
    queryFn: () => api.getDetectedDevices(),
    enabled: options?.enabled !== false,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: options?.refetchInterval ?? polling.detectedDevices,
  })
}

export function useDevicePolicies(deviceId: string, options?: { enabled?: boolean; refetchInterval?: number | false }) {
  return useQuery({
    queryKey: deviceKeys.policies(deviceId),
    queryFn: () => api.getDevicePolicies(deviceId),
    enabled: options?.enabled !== false && !!deviceId,
    refetchInterval: options?.refetchInterval,
  })
}

export function useDeviceStatus(deviceId: string, options?: { enabled?: boolean; refetchInterval?: number | false }) {
  return useQuery({
    queryKey: deviceKeys.status(deviceId),
    queryFn: () => api.getDeviceStatus(deviceId),
    enabled: options?.enabled !== false && !!deviceId,
    refetchInterval: options?.refetchInterval,
  })
}

export function useSuperviseDevice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (udid: string) => api.superviseDetectedDevice(udid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.detected() })
      queryClient.invalidateQueries({ queryKey: deviceKeys.list() })
    },
    onError: (err) => Sentry.captureException(err),
  })
}

export function useRemoveSupervision() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (deviceId: string) => api.removeSupervision(deviceId),
    onSuccess: (_, deviceId) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.detail(deviceId) })
      queryClient.invalidateQueries({ queryKey: deviceKeys.list() })
    },
    onError: (err) => Sentry.captureException(err),
  })
}

export function useApplyPolicy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ deviceId, policyId }: { deviceId: string; policyId: string }) =>
      api.applyPolicy(deviceId, policyId),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.policies(deviceId) })
      queryClient.invalidateQueries({ queryKey: deviceKeys.detail(deviceId) })
      queryClient.invalidateQueries({ queryKey: deviceKeys.list() })
    },
    onError: (err) => Sentry.captureException(err),
  })
}

export function useRemovePolicy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ deviceId, policyId }: { deviceId: string; policyId: string }) =>
      api.removePolicy(deviceId, policyId),
    onSuccess: (_, { deviceId }) => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.policies(deviceId) })
      queryClient.invalidateQueries({ queryKey: deviceKeys.detail(deviceId) })
      queryClient.invalidateQueries({ queryKey: deviceKeys.list() })
    },
    onError: (err) => Sentry.captureException(err),
  })
}
